import config from '@/Constants';
import { useAuth } from '@/hooks/Authentication/auth';
import { AxiosHelper } from '@/modules/shared/infra/protocols/http';
import axios from 'axios';
import MainScreen from '@/screens/Dashboard/Main';
import fetchDash from '@/services/query/fetchDash';
import fetchDashMenu from '@/services/query/fetchDashMenu';
import { useToast } from '@chakra-ui/react'; 
import html2canvas, { Options } from 'html2canvas';
import jsPDF, { jsPDFOptions } from 'jspdf';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ScreenshotContext from '@/screens/Dashboard/contexts/ScreenshotContext';
import { FeedbackError, FeedbackInfo } from '@components/Feedback';

interface HiddenDashboardPrinterProps {
  dashboardId: string;
  filters: string;
  onComplete: () => void;
  onClose: () => void;
  fileName?: string;
}

const HiddenDashboardPrinter: React.FC<HiddenDashboardPrinterProps> = ({
  dashboardId,
  filters,
  onComplete,
  onClose,
  fileName,
}) => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [cards, setCards] = useState<any>({});
  const [layouts, setLayouts] = useState<any>({ lg: [] });
  const [gridProps, setGridProps] = useState<any>({});
  const [dashName, setDashName] = useState('');
  const [dashPictureLogo, setDashPictureLogo] = useState<any>(null);
  const [filterEndpoint, setFilterEndpoint] = useState('');
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelTokenSource = useRef(axios.CancelToken.source());

  const parseFilters = (queryString: string) => {
    if (!queryString) return [];
    return []; 
  };

  const loadDashboard = useCallback(async () => {
    try {
      if (cancelTokenSource.current.token.reason) return;

      const params = {}; 

      const data = await fetchDash(dashboardId, params as any);
      console.log('HiddenPrinter fetchDash response:', data);

      if (!data || !data.cards) {
        console.error('HiddenPrinter: No cards in response', data);
        throw new Error('Dashboard not found or no cards returned');
      }

      setDashName(data.name);
      setDashPictureLogo(data.urlPictureLogo);

      const cardsWithId = Object.entries(data.cards).reduce((acc: any, [key, card]: [string, any]) => {
        const separator = card.url.includes('?') ? '&' : '?';
        acc[key] = {
            ...card,
            url: `${card.url}${separator}dashboardId=${dashboardId}`
        };
        return acc;
      }, {});

      setCards(cardsWithId);
      setLayouts(data.layouts);
      setFilterEndpoint(String(data.filterEndpoint));
      
      const { 
        hiddenFilter, filterPanel, navigationPanel, refreshCardTimout, 
        backgroundColor, borderColor, ...rest 
      } = data;
      
      setGridProps(rest);
      setMounted(true);
      setLoaded(true);

    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error('Failed to load hidden dashboard', error);
      FeedbackError({
        mainText: t('common.error'),
        subText: 'Falha ao carregar relatório para impressão',
      });
      onClose();
    }
  }, [dashboardId, filters, t, onClose]);

  useEffect(() => {
    if (dashboardId && dashboardId !== 'undefined') {
      loadDashboard();
    } else {
       console.error('Invalid dashboardId for HiddenPrinter:', dashboardId);
       onClose();
    }
  }, [dashboardId]);

  useEffect(() => {
    return () => {
      cancelTokenSource.current.cancel('Operation canceled by user.');
    };
  }, []);

  const generatePDF = useCallback(async () => {
    if (!containerRef.current) return;

    const waitForLoading = async () => {
      console.log('HiddenPrinter: Giving 2s for initial render...');
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const maxAttempts = 120;
      let attempts = 0;

      return new Promise<void>((resolve) => {
         const checkLoading = () => {

             if (cancelTokenSource.current.token.reason) {
                resolve();
                return;
             }

             attempts++;
             const loadingElements = containerRef.current?.querySelectorAll('.system-loading-indicator');
             const hasLoading = loadingElements && loadingElements.length > 0;
             
             console.log(`HiddenPrinter: Waiting for content... Attempt ${attempts}, Loading elements: ${loadingElements?.length || 0}`);

             if (!hasLoading || attempts >= maxAttempts) {
                 if (attempts >= maxAttempts) {
                    console.warn('HiddenPrinter: Timeout waiting for cards to load.');
                    FeedbackInfo({
                      mainText: 'Aviso',
                      subText: 'O tempo limite de carregamento foi atingido. O PDF pode estar incompleto.',
                    });
                 }
                 setTimeout(resolve, 1000); 
             } else {
                 setTimeout(checkLoading, 500);
             }
         };
         checkLoading();
      });
    };

    await waitForLoading();

    if (cancelTokenSource.current.token.reason) return; // Cancelado

    try {
      const options: Partial<Options> = {
        allowTaint: true,
        useCORS: true,
        scale: 2,
        windowWidth: 1280,
      };

      const canvas = await html2canvas(containerRef.current, options);
      
      const pdf = new jsPDF({
        orientation: canvas.height > canvas.width ? 'portrait' : 'landscape',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.8),
        'JPEG',
        0,
        0,
        canvas.width,
        canvas.height,
      );
      
      pdf.save(`${fileName || dashName || 'dashboard'}.pdf`);
      
      onComplete();
    } catch (error) {
      console.error('PDF Generation failed', error);
      FeedbackError({
        mainText: t('common.error'),
        subText: t('messageEdit.fileDownloadError'),
      });
      onClose();
    }
  }, [loaded, fileName, dashName, onComplete, onClose, t]);

  useEffect(() => {
    if (loaded && mounted) {
      generatePDF();
    }
  }, [loaded, mounted]);

  const screenshotContextValue = React.useMemo(() => ({
    isCapturingScreenshot: true,
    setCapturingScreenshot: () => {}
  }), []);

  if (!mounted) return null;

  return (
    <div 
      style={{ 
        position: 'fixed', 
        left: '-10000px', 
        top: 0, 
        width: '1280px', 
        height: '2500px',
        zIndex: -1000,
        overflow: 'hidden',
        background: '#f4f6f8'
      }}
      ref={containerRef}
    >
      <ScreenshotContext.Provider value={screenshotContextValue}>
        <MainScreen
          dashName={dashName}
          dashPictureLogo={dashPictureLogo}
          dashId={dashboardId}
          filterEndpoint={filterEndpoint}
          isLoadingContent={false}
          filters={filters ? `&${filters.replace(/^\?/, '')}` : ''}
          mounted={mounted}
          cards={cards}
          layouts={layouts}
          gridProps={gridProps}
          isMobile={false}
          getFormattedFilter={() => {}}
          onFilter={() => {}}
          withoutDashs={false}
          onBreakpointChange={() => {}}
          onLayoutChange={() => {}}
          openFilters={() => {}}
          MenuFilterComponent={() => <></>}
          dashMenuOptions={[]}
          dashMenuPath={{ path: '', setPath: () => {} }}
          useDashMenu={{ state: {} as any, setState: () => {} }}
          hiddenFilter={true}
          filterPanel={false}
          navigationPanel={false}
          currentSuperGroup={{ id: '', name: '' }}
          openSelectSuperGroup={() => {}}
          hiddenSupergroup={true}
          cancelToken={cancelTokenSource.current.token}
          backgroundColor="#ffffff"
          borderColor="#e2e8f0"
          initialFilters={[]}
          refreshCardTimout={null}
          enableScrollBar={false}
        />
      </ScreenshotContext.Provider>
    </div>
  );
};

export default HiddenDashboardPrinter;
